/// <reference lib="dom" />
/**
 * webShipDraw.web.ts
 * Canvas2D helpers for drawing spaceships and their fire/smoke trails.
 *
 * Web-only: this file is `.web.ts` so Metro never bundles it on native.
 *
 * Phase 3 — Art, Audio & Polish.
 *
 * @module webShipDraw
 */

import {
  drawFighter,
  drawFreighter,
  drawInterceptor,
  drawBomber,
  drawCorvette,
} from './webShipVariants';

/** Ship hull variant — each draws a distinct silhouette. */
export type ShipVariant = 0 | 1 | 2 | 3 | 4;

/** Data for a single spaceship in the sky. */
export interface SkyShip {
  x: number;
  y: number;
  size: number;
  speed: number;
  flip: boolean;
  onFire: boolean;
  /** Hull design variant (0–4). */
  variant: ShipVariant;
  /** True for ships that are ascending (taking off). */
  rising: boolean;
  /** Per-ship fall speed multiplier for burning ships (1 = default). */
  fallSpeed: number;
}

/**
 * Draw a single spaceship using one of 5 hull variants.
 *
 * @param ctx - Canvas 2D rendering context.
 * @param s - Ship data.
 * @param sx - Screen X position.
 * @param sy - Screen Y position.
 */
export function drawShip(
  ctx: CanvasRenderingContext2D,
  s: SkyShip,
  sx: number,
  sy: number,
): void {
  ctx.save();
  ctx.translate(sx, sy);
  if (s.flip) ctx.scale(-1, 1);

  const sz = s.size;
  const damaged = s.onFire;

  // DECISION: Shared outline setup — every variant gets a dark edge for solidity
  ctx.lineWidth = Math.max(0.5, sz * 0.06);
  ctx.strokeStyle = damaged ? 'rgba(60,40,30,0.9)' : 'rgba(30,40,60,0.8)';
  ctx.lineJoin = 'round';

  switch (s.variant) {
    case 0: drawFighter(ctx, sz, damaged); break;
    case 1: drawFreighter(ctx, sz, damaged); break;
    case 2: drawInterceptor(ctx, sz, damaged); break;
    case 3: drawBomber(ctx, sz, damaged); break;
    case 4: drawCorvette(ctx, sz, damaged); break;
  }

  ctx.restore();
}

/**
 * Draw fire and smoke trail behind a damaged ship.
 *
 * @param ctx - Canvas 2D rendering context.
 * @param s - Ship data.
 * @param sx - Screen X position of the ship.
 * @param sy - Screen Y position of the ship.
 * @param time - Accumulated animation time in seconds.
 */
export function drawFireTrail(
  ctx: CanvasRenderingContext2D,
  s: SkyShip,
  sx: number,
  sy: number,
  time: number,
): void {
  const dir = s.flip ? 1 : -1;
  const trailX = sx + dir * s.size * 2;

  // Smoke puffs — 5 puffs trailing behind
  for (let i = 0; i < 5; i++) {
    const age = (time * 2 + i * 0.7 + s.x) % 3.5;
    const puffX = trailX + dir * age * 12;
    const puffY = sy - age * 3 + Math.sin(time * 3 + i) * 1.5;
    const puffR = Math.max(0.5, 2 + age * 2.5);
    const alpha = Math.max(0, 0.4 - age * 0.12);
    ctx.fillStyle = `rgba(80,80,80,${alpha.toFixed(2)})`;
    ctx.beginPath();
    ctx.arc(puffX, puffY, puffR, 0, Math.PI * 2);
    ctx.fill();
  }

  // Fire flicker at the ship body
  for (let i = 0; i < 3; i++) {
    const fX = trailX + dir * (Math.sin(time * 8 + i * 2) * 3);
    const fY = sy + Math.cos(time * 6 + i) * 2;
    const fR = Math.max(0.5, 1.5 + Math.sin(time * 10 + i * 3) * 0.8);
    const r = 255;
    const g = Math.floor(120 + Math.sin(time * 12 + i) * 60);
    ctx.fillStyle = `rgba(${r},${g},20,0.7)`;
    ctx.beginPath();
    ctx.arc(fX, fY, fR, 0, Math.PI * 2);
    ctx.fill();
  }
}
